import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, LogOut, CheckCheck, Trash2, ChevronDown, Send, Mail, Paperclip, Archive, RotateCcw, StickyNote, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const fmtDate = (iso) => {
  const s = typeof iso === "string" && !/Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso + "Z" : iso;
  return new Date(s).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    timeZone: "Europe/Rome",
  });
};

const STATUS_OPTIONS = [
  { value: "nuova", label: "Nuova", cls: "text-gold border-gold/40" },
  { value: "da_contattare", label: "Da contattare", cls: "text-amber-400 border-amber-400/40" },
  { value: "contattato", label: "Contattato", cls: "text-sky-400 border-sky-400/40" },
  { value: "sopralluogo", label: "Sopralluogo", cls: "text-cyan-400 border-cyan-400/40" },
  { value: "preventivo_da_preparare", label: "Preventivo da preparare", cls: "text-indigo-400 border-indigo-400/40" },
  { value: "preventivo_inviato", label: "Preventivo inviato", cls: "text-emerald-400 border-emerald-400/40" },
  { value: "in_trattativa", label: "In trattativa", cls: "text-violet-400 border-violet-400/40" },
  { value: "accettato", label: "Accettato", cls: "text-green-400 border-green-400/40" },
  { value: "rifiutato", label: "Rifiutato", cls: "text-red-400 border-red-400/40" },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("star_admin_token"));
  const [quotes, setQuotes] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("tutte");
  const [replyText, setReplyText] = useState({});
  const [sending, setSending] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [search, setSearch] = useState("");
  const [notesDraft, setNotesDraft] = useState({});
  const [savingNotes, setSavingNotes] = useState(null);

  useEffect(() => {
    if (!expanded || !quotes || !token) return;
    const q = quotes.find((x) => x.id === expanded);
    if (!q?.attachment?.content_type?.startsWith("image/") || thumbs[expanded]) return;
    api
      .get(`/quotes/${expanded}/attachment`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => setThumbs((t) => ({ ...t, [expanded]: URL.createObjectURL(res.data) })))
      .catch(() => {});
  }, [expanded, quotes, token, thumbs]);

  const loadQuotes = useCallback(
    async (t) => {
      try {
        const { data } = await api.get("/quotes", { headers: { Authorization: `Bearer ${t}` } });
        setQuotes(data);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("star_admin_token");
          setToken(null);
        } else {
          toast.error("Errore nel caricamento delle richieste. Riprova.");
        }
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadQuotes(token);
  }, [token, loadQuotes]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("star_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("star_admin_token");
    setToken(null);
    setQuotes(null);
  };

  const markRead = async (id) => {
    await api.patch(`/quotes/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, read: true } : x)));
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, status, read: status !== "nuova" ? true : x.read } : x)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Aggiornamento stato fallito");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta? L'operazione non è reversibile.")) return;
    await api.delete(`/quotes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setQuotes((q) => q.filter((x) => x.id !== id));
    toast.success("Richiesta eliminata");
  };

  const setArchived = async (id, archived) => {
    try {
      await api.patch(`/quotes/${id}/archive`, { archived }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, is_archived: archived } : x)));
      toast.success(archived ? "Richiesta archiviata" : "Richiesta ripristinata");
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const saveNotes = async (id) => {
    setSavingNotes(id);
    try {
      const internal_notes = notesDraft[id] ?? "";
      await api.patch(`/quotes/${id}`, { internal_notes }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, internal_notes } : x)));
      toast.success("Note salvate");
    } catch {
      toast.error("Salvataggio note non riuscito");
    } finally {
      setSavingNotes(null);
    }
  };

  const openAttachment = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Impossibile aprire l'allegato");
    }
  };

  const sendReply = async (id) => {    const message = (replyText[id] || "").trim();
    if (message.length < 2) {
      toast.error("Scrivi un messaggio prima di inviare");
      return;
    }
    setSending(id);
    try {
      const { data } = await api.post(
        `/quotes/${id}/reply`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuotes((qs) =>
        qs.map((x) =>
          x.id === id
            ? {
                ...x,
                read: true,
                status: !x.status || x.status === "nuova" ? "contattato" : x.status,
                replies: [...(x.replies || []), data.reply],
              }
            : x
        )
      );
      setReplyText((rt) => ({ ...rt, [id]: "" }));
      toast.success("Risposta inviata via email");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Invio fallito. Riprova.");
    } finally {
      setSending(null);
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.form
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="w-full max-w-md bg-surface border border-white/10 p-10 flex flex-col gap-6"
          data-testid="admin-login-form"
        >
          <div className="flex justify-center mb-2">
            <img src="/logo-dark.png" alt="Star Edil srls" className="h-16 w-auto" data-testid="admin-login-logo" />
          </div>
          <p className="font-oswald uppercase text-xs tracking-[0.3em] text-muted-foreground">Area riservata</p>
          <div>
            <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Email</label>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-elevated border-white/10 rounded-none h-12 focus-visible:ring-gold"
              data-testid="admin-input-email"
            />
          </div>
          <div>
            <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Password</label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-elevated border-white/10 rounded-none h-12 focus-visible:ring-gold"
              data-testid="admin-input-password"
            />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="bg-gold text-ink font-oswald uppercase tracking-[0.15em] px-9 py-4 flex items-center justify-center gap-3 hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Accedi
          </button>
          <a href="/" className="text-xs text-muted-foreground hover:text-gold transition-colors text-center" data-testid="admin-back-home">
            ← Torna al sito
          </a>
        </motion.form>
      </main>
    );
  }

  const unread = quotes?.filter((q) => !q.read && !q.is_archived).length ?? 0;
  const statusOf = (q) => q.status || "nuova";
  const matchesSearch = (q) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return [q.name, q.email, q.phone, q.service].some((v) => (v || "").toLowerCase().includes(s));
  };
  const active = quotes ? quotes.filter((q) => !q.is_archived) : [];
  const archived = quotes ? quotes.filter((q) => q.is_archived) : [];
  const pool = filter === "archiviate" ? archived : filter === "tutte" ? active : active.filter((q) => statusOf(q) === filter);
  const filtered = quotes ? pool.filter(matchesSearch) : null;
  const countFor = (v) =>
    v === "archiviate"
      ? archived.length
      : v === "tutte"
      ? active.length
      : active.filter((q) => statusOf(q) === v).length;

  return (
    <main className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/logo-dark.png"
              alt="Star Edil srls"
              className="h-11 md:h-12 w-auto"
              data-testid="admin-logo"
            />
            <span className="pl-4 border-l border-white/10 font-oswald uppercase text-xs tracking-[0.3em] text-muted-foreground hidden sm:block">
              Richieste preventivo
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="font-oswald text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="admin-stats">
              {quotes?.length ?? 0} totali · <span className="text-gold">{unread} da leggere</span>
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12">
        {quotes !== null && quotes.length > 0 && (
          <div className="mb-8 flex flex-col gap-5" data-testid="admin-controls">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, email, telefono o intervento..."
                data-testid="admin-search-input"
                className="bg-elevated border-white/10 rounded-none h-12 pl-11 focus-visible:ring-gold"
              />
            </div>
            <div className="flex flex-wrap gap-3" data-testid="admin-status-filters">
              {[{ value: "tutte", label: "Tutte" }, ...STATUS_OPTIONS, { value: "archiviate", label: "Archiviate" }].map((f) => (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  data-testid={`admin-filter-${f.value}`}
                  className={`font-oswald uppercase text-xs tracking-[0.15em] px-4 py-2 border transition-colors duration-300 ${
                    filter === f.value
                      ? "bg-gold text-ink border-gold"
                      : "border-white/10 text-muted-foreground hover:border-gold hover:text-gold"
                  }`}
                >
                  {f.label} <span className="opacity-70">({countFor(f.value)})</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {quotes === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-gold" />
          </div>
        ) : quotes.length === 0 ? (
          <p className="text-center py-24 text-muted-foreground font-oswald uppercase tracking-[0.2em]" data-testid="admin-empty-state">
            Nessuna richiesta ricevuta
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-24 text-muted-foreground font-oswald uppercase tracking-[0.2em]" data-testid="admin-filter-empty-state">
            Nessuna richiesta trovata
          </p>
        ) : (
          <div className="border border-white/10" data-testid="admin-quotes-table">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-surface border-b border-white/10 font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground">
              <span className="col-span-2">Data</span>
              <span className="col-span-2">Nome</span>
              <span className="col-span-2">Contatti</span>
              <span className="col-span-2">Intervento</span>
              <span className="col-span-2">Stato</span>
              <span className="col-span-2 text-right">Azioni</span>
            </div>
            {filtered.map((q) => (
              <div key={q.id} className="border-b border-white/10 last:border-b-0" data-testid={`admin-quote-row-${q.id}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                  onKeyDown={(e) => e.key === "Enter" && setExpanded(expanded === q.id ? null : q.id)}
                  className={`w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 text-left cursor-pointer transition-colors duration-300 hover:bg-surface ${q.read ? "" : "bg-surface/50"}`}
                  data-testid={`admin-quote-expand-${q.id}`}
                >
                  <span className="md:col-span-2 text-sm text-muted-foreground">{fmtDate(q.created_at)}</span>
                  <span className="md:col-span-2 text-sm font-semibold flex items-center gap-2">
                    {!q.read && <span className="w-2 h-2 rounded-full bg-gold shrink-0" data-testid={`admin-unread-dot-${q.id}`} />}
                    {q.name}
                  </span>
                  <span className="md:col-span-2 text-sm text-muted-foreground truncate">
                    {q.email} · {q.phone}
                  </span>
                  <span className="md:col-span-2 text-sm text-gold font-oswald tracking-wide">{q.service}</span>
                  <span className="md:col-span-2 flex items-center" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={q.status || "nuova"}
                      onChange={(e) => changeStatus(q.id, e.target.value)}
                      data-testid={`admin-status-select-${q.id}`}
                      className={`bg-elevated border px-2 py-1.5 text-xs font-oswald uppercase tracking-[0.1em] cursor-pointer focus:outline-none ${
                        (STATUS_OPTIONS.find((s) => s.value === (q.status || "nuova")) || STATUS_OPTIONS[0]).cls
                      }`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value} className="bg-elevated text-foreground">
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </span>
                  <span className="md:col-span-2 flex md:justify-end items-center gap-3">
                    {!q.read && !q.is_archived && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); markRead(q.id); }}
                        onKeyDown={(e) => e.key === "Enter" && markRead(q.id)}
                        data-testid={`admin-mark-read-${q.id}`}
                        className="p-2 border border-white/10 text-muted-foreground hover:text-gold hover:border-gold transition-colors"
                        title="Segna come letta"
                      >
                        <CheckCheck className="w-4 h-4" />
                      </span>
                    )}
                    {q.is_archived ? (
                      <>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setArchived(q.id, false); }}
                          onKeyDown={(e) => e.key === "Enter" && setArchived(q.id, false)}
                          data-testid={`admin-restore-${q.id}`}
                          className="p-2 border border-white/10 text-muted-foreground hover:text-emerald-400 hover:border-emerald-400 transition-colors"
                          title="Ripristina"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); remove(q.id); }}
                          onKeyDown={(e) => e.key === "Enter" && remove(q.id)}
                          data-testid={`admin-delete-${q.id}`}
                          className="p-2 border border-white/10 text-muted-foreground hover:text-red-400 hover:border-red-400 transition-colors"
                          title="Elimina definitivamente"
                        >
                          <Trash2 className="w-4 h-4" />
                        </span>
                      </>
                    ) : (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setArchived(q.id, true); }}
                        onKeyDown={(e) => e.key === "Enter" && setArchived(q.id, true)}
                        data-testid={`admin-archive-${q.id}`}
                        className="p-2 border border-white/10 text-muted-foreground hover:text-gold hover:border-gold transition-colors"
                        title="Archivia"
                      >
                        <Archive className="w-4 h-4" />
                      </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-300 ${expanded === q.id ? "rotate-180" : ""}`} />
                  </span>
                </div>
                {expanded === q.id && (
                  <div className="px-6 pb-6 pt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 bg-surface/30" data-testid={`admin-quote-detail-${q.id}`}>
                    <div>
                      <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-2">Metratura</p>
                      <p className="text-sm text-muted-foreground">{q.area || "—"}</p>
                    </div>
                    <div>
                      <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-2">Tempistiche</p>
                      <p className="text-sm text-muted-foreground">{q.timing || "—"}</p>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-1">
                      <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-2">Messaggio</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{q.message || "—"}</p>
                    </div>
                    {q.attachment && (
                      <div className="sm:col-span-2 lg:col-span-3">
                        <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-2">Allegato</p>
                        {q.attachment.content_type?.startsWith("image/") &&
                          (thumbs[q.id] ? (
                            <button
                              onClick={() => openAttachment(q)}
                              data-testid={`admin-attachment-thumb-${q.id}`}
                              className="block mb-3 border border-white/10 hover:border-gold transition-colors duration-300"
                              title="Apri a schermo intero"
                            >
                              <img src={thumbs[q.id]} alt={q.attachment.filename} className="h-44 w-auto max-w-full object-cover" />
                            </button>
                          ) : (
                            <div className="h-44 w-60 mb-3 border border-white/10 flex items-center justify-center" data-testid={`admin-attachment-thumb-loading-${q.id}`}>
                              <Loader2 className="w-5 h-5 animate-spin text-gold" />
                            </div>
                          ))}
                        <button
                          onClick={() => openAttachment(q)}
                          data-testid={`admin-attachment-${q.id}`}
                          className="flex items-center gap-3 border border-white/10 px-5 py-3 text-sm text-muted-foreground hover:border-gold hover:text-gold transition-colors duration-300"
                        >
                          <Paperclip className="w-4 h-4 text-gold" />
                          {q.attachment.filename}
                          <span className="text-xs text-muted-foreground/60">
                            ({Math.round((q.attachment.size || 0) / 1024)} KB — apri/scarica)
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {expanded === q.id && (
                  <div className="px-6 pb-6 bg-surface/30" data-testid={`admin-notes-box-${q.id}`}>
                    <div className="border-t border-white/10 pt-6">
                      <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-3 flex items-center gap-2">
                        <StickyNote className="w-4 h-4" /> Note interne <span className="text-muted-foreground/50 normal-case tracking-normal">(visibili solo in quest'area)</span>
                      </p>
                      <Textarea
                        value={notesDraft[q.id] ?? q.internal_notes ?? ""}
                        onChange={(e) => setNotesDraft((n) => ({ ...n, [q.id]: e.target.value }))}
                        placeholder="Appunti interni sulla richiesta, promemoria, dettagli sopralluogo..."
                        className="bg-elevated border-white/10 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-gold rounded-none min-h-24 mb-3"
                        data-testid={`admin-notes-input-${q.id}`}
                      />
                      <button
                        onClick={() => saveNotes(q.id)}
                        disabled={savingNotes === q.id}
                        data-testid={`admin-notes-save-${q.id}`}
                        className="border border-white/15 text-muted-foreground font-oswald uppercase tracking-[0.15em] text-xs px-6 py-2.5 flex items-center gap-2 hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-60"
                      >
                        {savingNotes === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
                        Salva note
                      </button>
                    </div>
                  </div>
                )}
                {expanded === q.id && (
                  <div className="px-6 pb-8 bg-surface/30" data-testid={`admin-reply-box-${q.id}`}>
                    <div className="border-t border-white/10 pt-6">
                      <p className="font-oswald uppercase text-xs tracking-[0.2em] text-gold mb-4 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Rispondi via email a {q.email}
                      </p>
                      {q.replies?.length > 0 && (
                        <div className="mb-5 flex flex-col gap-3" data-testid={`admin-reply-history-${q.id}`}>
                          {q.replies.map((r, i) => (
                            <div key={i} className="border-l-2 border-gold pl-4 py-1">
                              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{r.message}</p>
                              <p className="text-xs text-muted-foreground/60 mt-1 font-oswald tracking-[0.15em] uppercase">
                                Inviata il {fmtDate(r.sent_at)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      <Textarea
                        value={replyText[q.id] || ""}
                        onChange={(e) => setReplyText((rt) => ({ ...rt, [q.id]: e.target.value }))}
                        placeholder="Scrivi la risposta... il cliente la riceverà via email firmata Star Edil srls"
                        className="bg-elevated border-white/10 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-gold rounded-none min-h-28 mb-4"
                        data-testid={`admin-reply-input-${q.id}`}
                      />
                      <button
                        onClick={() => sendReply(q.id)}
                        disabled={sending === q.id}
                        data-testid={`admin-reply-send-${q.id}`}
                        className="btn-tracer bg-gold text-ink font-oswald uppercase tracking-[0.15em] text-sm px-7 py-3 flex items-center gap-3 hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
                      >
                        {sending === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sending === q.id ? "Invio in corso..." : "Invia risposta"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
